import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api, fmtEur, fmtPct, WEEKDAYS_SK } from "@/lib/app";
import { ArrowLeft, Storefront } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STORE_COLORS = ["#2B4C38", "#C78220", "#3E7B52", "#BA3F2B", "#7FAB8C", "#1E3627", "#E09B6E", "#5E6962"];

export default function Aggregate() {
  const { data: stores = [] } = useSWR("/stores", fetcher);
  const [selected, setSelected] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (stores.length && selected.length === 0) {
      setSelected(stores.map((s) => s.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores.length]);

  // determine overall range across selected stores
  const rangeUrls = selected.map((id) => `/stores/${id}/data-range`);
  const { data: ranges } = useSWR(
    rangeUrls.length ? rangeUrls.join("|") : null,
    async () => {
      const res = await Promise.all(rangeUrls.map((u) => api.get(u).then((r) => r.data)));
      return res;
    },
  );

  useEffect(() => {
    if (ranges?.length && !from) {
      const mins = ranges.filter((r) => r.min).map((r) => r.min).sort();
      const maxs = ranges.filter((r) => r.max).map((r) => r.max).sort();
      if (mins.length) setFrom(mins[0]);
      if (maxs.length) setTo(maxs[maxs.length - 1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranges]);

  const ids = selected.join(",");
  const qs = ids && from && to ? `store_ids=${ids}&period_from=${from}&period_to=${to}` : null;
  const { data } = useSWR(qs ? `/aggregate/overview?${qs}` : null, fetcher);

  const chartData = useMemo(() => {
    if (!data?.stores) return [];
    return WEEKDAYS_SK.map((name, wd) => {
      const row = { name };
      for (const s of data.stores) {
        if (s.empty) continue;
        const w = s.by_weekday.find((x) => x.weekday === wd);
        row[`s_${s.store_code}`] = w ? w.avg_daily_revenue : 0;
      }
      return row;
    });
  }, [data]);

  const toggleStore = (id) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4" data-testid="back-link">
        <ArrowLeft size={14} /> Späť na strediská
      </Link>

      <div className="mb-6">
        <p className="label-eyebrow mb-1">Agregát</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">Porovnanie stredísk</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Bok po boku KPIs a tržby cez vybrané prevádzky.</p>
      </div>

      {stores.length < 2 && (
        <div className="card-flat p-6 text-sm text-[var(--text-secondary)]">
          Pre agregát potrebuješ aspoň 2 strediská s naimportovanými dátami.
          <div className="mt-2">
            <Link to="/" className="text-[var(--brand)] underline">Vytvor ďalšie stredisko</Link>
          </div>
        </div>
      )}

      <div className="card-flat p-4 mb-5">
        <p className="label-eyebrow mb-2">Vyberte strediská</p>
        <div className="flex flex-wrap gap-2">
          {stores.map((s) => {
            const active = selected.includes(s.id);
            return (
              <button
                key={s.id}
                onClick={() => toggleStore(s.id)}
                data-testid={`agg-toggle-${s.code}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
                  active ? "bg-[var(--brand)] text-white" : "btn-ghost"
                }`}
              >
                <Storefront size={12} />
                {s.code} · {s.name}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <div>
            <label className="label-eyebrow block mb-1">Obdobie od</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="agg-from" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Do</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="agg-to" />
          </div>
        </div>
      </div>

      {!data && qs && <div className="text-[var(--text-secondary)]">Načítavam…</div>}

      {data?.stores?.length > 0 && (
        <>
          <div className="card-flat overflow-hidden mb-5">
            <div className="px-5 pt-5 pb-3">
              <h3 className="font-display text-base font-semibold">KPI bok po boku</h3>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Stredisko</th>
                  <th className="text-right">Tržba</th>
                  <th className="text-right">Priem. denne</th>
                  <th className="text-right">Os.hod</th>
                  <th className="text-right">€/os.hod</th>
                  <th className="text-right">Požad.</th>
                  <th className="text-right">Efektívnosť</th>
                  <th className="text-right">Dní</th>
                </tr>
              </thead>
              <tbody>
                {data.stores.map((s) => {
                  if (s.empty)
                    return (
                      <tr key={s.store_id}>
                        <td className="font-medium">
                          <Link to={`/strediska/${s.store_id}`} className="hover:underline">{s.store_code} · {s.store_name}</Link>
                        </td>
                        <td colSpan={7} className="text-[var(--text-muted)] italic">Bez dát v zvolenom období</td>
                      </tr>
                    );
                  const t = s.totals;
                  const tone =
                    t.efficiency_pct >= 100 ? "status-good" : t.efficiency_pct >= 70 ? "status-warn" : "status-bad";
                  return (
                    <tr key={s.store_id}>
                      <td className="font-medium">
                        <Link to={`/strediska/${s.store_id}`} className="hover:underline">{s.store_code} · {s.store_name}</Link>
                      </td>
                      <td className="text-right tabular">{fmtEur(t.revenue)}</td>
                      <td className="text-right tabular">{fmtEur(t.avg_daily_revenue)}</td>
                      <td className="text-right tabular">{Math.round(t.person_hours)}</td>
                      <td className="text-right tabular">{fmtEur(t.avg_per_person_hour)}</td>
                      <td className="text-right tabular text-[var(--text-muted)]">{fmtEur(t.required_performance)}</td>
                      <td className={`text-right tabular px-2 rounded ${tone}`}>{fmtPct(t.efficiency_pct, 1)}</td>
                      <td className="text-right tabular">{t.unique_days}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card-flat p-5">
            <h3 className="font-display text-base font-semibold mb-1">Priem. denná tržba podľa dňa v týždni</h3>
            <p className="text-xs text-[var(--text-secondary)] mb-4">Bok po boku pre každé stredisko.</p>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6962" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
                <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {data.stores.filter((s) => !s.empty).map((s, idx) => (
                  <Bar
                    key={s.store_id}
                    dataKey={`s_${s.store_code}`}
                    name={s.store_name}
                    fill={STORE_COLORS[idx % STORE_COLORS.length]}
                    radius={[3, 3, 0, 0]}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
